import { RouteLocationRaw } from 'vue-router'

export type NavigationGroup = {
  title: string
  icon: string
  children: NavigationItem[]
  group: true
  order: number
  loading: boolean
}

export type NavigationItem = {
  icon: string
  title: string
  to: RouteLocationRaw
  shortTitle?: string
  priorityItemMobile?: boolean
  priorityOrderMobile?: number
}

const NAVIGATION_ITEMS = {
  artifacts: {
    title: 'navigation_drawer.artifacts',
    to: { name: 'Artifacts' },
    icon: 'mdi-image-area',
  },
  authorizationManagement: {
    title: 'navigation_drawer.authorization-management',
    to: { name: 'Authorization Management' },
    icon: 'mdi-account-key',
  },
  displayModeManagement: {
    title: 'navigation_drawer.displayModeManagement',
    to: { name: 'Display Mode Management' },
    icon: 'mdi-account-cog-outline',
  },
  barnDocumentation: {
    title: 'navigation_drawer.barn-documentation',
    to: { name: 'Barn Documentation' },
    icon: 'mdi-file-document-edit',
  },
  barnManagementDashboard: {
    title: 'navigation_drawer.dashboards.barn-management',
    shortTitle: 'navigation_drawer.short-titles.barn-management',
    to: { name: 'Barn Management Dashboard' },
    icon: 'mdi-pitchfork',
  },
  barnInterventions: {
    title: 'navigation_drawer.interventions',
    to: { name: 'BarnInterventions' },
    icon: 'mdi-calendar-check',
  },
  cameraOverview: {
    title: 'navigation_drawer.camera-overview',
    to: { name: 'Camera Overview' },
    icon: 'mdi-cctv',
  },
  defaultCameraCanvas: {
    title: 'navigation_drawer.default-canvas',
    shortTitle: 'navigation_drawer.short-titles.default-canvas',
    to: { name: 'CameraCanvas', params: {} },
    icon: 'mdi-artboard',
  },
  CameraList: {
    title: 'router_elements.cameras',
    to: { name: 'CameraList' },
    icon: 'mdi-cctv',
  },
  canvasList: {
    title: 'navigation_drawer.more_canvases',
    to: { name: 'canvasList' },
    icon: 'mdi-group',
  },
  chartSharingCreator: {
    title: 'navigation_drawer.chart_sharing',
    to: { name: 'Chart Sharing Creator' },
    icon: 'mdi-chart-timeline-variant-shimmer',
  },
  config: {
    title: 'navigation_drawer.config',
    to: { name: 'Config' },
    icon: 'mdi-cogs',
  },
  overviewDashboard: {
    title: 'navigation_drawer.dashboards.dashboard_overview',
    shortTitle: 'navigation_drawer.short-titles.dashboard_overview',
    to: { name: 'Dashboard Overview' },
    icon: 'mdi-monitor-dashboard',
  },
  'Data Collector Annotations': {
    title: 'navigation_drawer.data_collector.annotation_list',
    to: { name: 'Data Collector Annotations' },
    icon: 'mdi-list-box-outline',
  },
  'DataCollector Records': {
    title: 'navigation_drawer.data_collector.record_list',
    to: { name: 'DataCollector Records' },
    icon: 'mdi-list-status',
  },
  DataCollectorFileUpload: {
    title: 'navigation_drawer.data_collector.file_upload',
    to: { name: 'DataCollectorFileUpload' },
    icon: 'mdi-upload',
  },
  DataCollectorOverview: {
    title: 'navigation_drawer.data_collector.data_collector',
    to: { name: 'DataCollectorOverview' },
    icon: 'mdi-view-dashboard',
  },
  deadAnimalDashboard: {
    title: 'navigation_drawer.dashboards.dead-animal-statistics',
    to: { name: 'Dead Animals Dashboard' },
    icon: 'mdi-skull-crossbones',
  },
  eulas: {
    title: 'navigation_drawer.eulas',
    to: { name: 'Eulas' },
    icon: 'mdi-signature',
  },
  frameSeek: {
    title: 'navigation_drawer.frame-seek',
    to: { name: 'Frame Seek' },
    icon: 'mdi-fast-forward',
  },
  incidents: {
    title: 'navigation_drawer.incidents',
    to: { name: 'Incidents' },
    icon: 'mdi-alert',
  },
  licenses: {
    title: 'navigation_drawer.licenses',
    to: { name: 'Licenses' },
    icon: 'mdi-license',
  },
  measurementsDashboard: {
    title: 'navigation_drawer.dashboards.measurements',
    to: { name: 'Measurements Dashboard' },
    icon: 'mdi-chart-line',
  },
  networkDiscovery: {
    title: 'navigation_drawer.network-discover',
    to: { name: 'Network Discovery' },
    icon: 'mdi-network',
  },
  networkManagement: {
    title: 'navigation_drawer.network_management',
    to: { name: 'Network Management' },
    icon: 'mdi-router-wireless',
  },
  opsDashboard: {
    title: 'navigation_drawer.dashboards.ops',
    to: { name: 'Ops Dashboard' },
    icon: 'mdi-shield-check',
  },
  penObservation: {
    title: 'navigation_drawer.pen_observations',
    to: { name: 'Pen Observation' },
    icon: 'mdi-camera-document',
  },
  ProjectStatistics: {
    title: 'navigation_drawer.data_collector.project-statistics',
    to: { name: 'ProjectStatistics' },
    icon: 'mdi-chart-line',
  },
  quickArtifactDashboard: {
    title: 'navigation_drawer.dashboards.quick-artifacts',
    to: { name: 'Quick Artifact Dashboard' },
    icon: 'mdi-flash',
  },
  recordingExport: {
    title: 'navigation_drawer.recording-export',
    to: { name: 'Recording Export' },
    icon: 'mdi-export',
  },
  recordingSaving: {
    title: 'navigation_drawer.save-recordings',
    to: { name: 'Recording Saving' },
    icon: 'mdi-content-save-plus',
  },
  recordings: {
    title: 'navigation_drawer.recordings',
    to: { name: 'Recordings' },
    icon: 'mdi-video-image',
  },
  regionEditor: {
    title: 'navigation_drawer.region-management',
    to: { name: 'Region Editor' },
    icon: 'mdi-vector-polygon',
  },
  regionOverviewDashboard: {
    title: 'navigation_drawer.dashboards.region-overview',
    shortTitle: 'navigation_drawer.short-titles.region-overview',
    to: { name: 'Region Overview' },
    icon: 'mdi-view-dashboard-variant',
  },
  fatteningOverview: {
    title: 'navigation_drawer.dashboards.fattening-overview',
    shortTitle: 'navigation_drawer.short-titles.fattening-overview',
    to: { name: 'Fattening Overview' },
    icon: 'mdi-view-dashboard',
  },
  senatorOverview: {
    title: 'router_elements.senator',
    to: { name: 'Senator Overview' },
    icon: 'mdi-account',
  },
  vmsInstanceList: {
    title: 'navigation_drawer.vmsInstances',
    to: { name: 'VMSInstanceList' },
    icon: 'mdi-list-status',
  },
  vmsInstancesContractInformation: {
    title: 'navigation_drawer.vms_instances_contract_information',
    to: { name: 'VmsInstancesContractInformation' },
    icon: 'mdi-currency-eur',
  },
  weights: {
    title: 'navigation_drawer.weights',
    to: { name: 'Weights' },
    icon: 'mdi-weight-kilogram',
  },
  reportSetup: {
    title: 'navigation_drawer.report_setup',
    to: { name: 'Report Setup' },
    icon: 'mdi-file-chart',
  },
  reportList: {
    title: 'navigation_drawer.report_list',
    to: { name: 'Reports' },
    icon: 'mdi-file-chart',
  },
  dashboardList: {
    title: 'navigation_drawer.dashboards.dashboard_list',
    to: { name: 'Dashboards' },
    icon: 'mdi-view-dashboard',
  },
  llmGenerate: {
    title: 'router_elements.llm.generate',
    to: { name: 'LLM Generate' },
    icon: 'mdi-creation',
  },
  llmSystemPrompts: {
    title: 'router_elements.llm.system_prompts',
    to: { name: 'LLM System Prompts' },
    icon: 'mdi-message-text',
  },
  llmChats: {
    title: 'router_elements.llm.chats',
    to: { name: 'LLM Chats' },
    icon: 'mdi-chat',
  },
  llmTools: {
    title: 'router_elements.llm.tools',
    to: { name: 'LLM Tools' },
    icon: 'mdi-tools',
  },
} as const satisfies Record<string, NavigationItem>

export { NAVIGATION_ITEMS }
